/**
 * Canonical formatters for dates, credits, currency, durations, and sizes.
 * Strictly adheres to British English (en-GB):
 * - Dates in DD/MM/YYYY format
 * - Times in 24-hour UTC format
 * - Numbers with en-GB thousands separators
 * - En dashes (–) instead of em dashes or hyphens for ranges and empty values
 */

/** 12480 becomes "12,480". en-GB grouping, never a currency sign. */
export function formatCredits(credits: number): string {
  return credits.toLocaleString("en-GB");
}

/** Formats a credit amount as its USD equivalent (1 credit = $0.01). */
export function formatCreditsUsd(credits: number): string {
  const dollars = Math.abs(credits) * 0.01;
  return `$${dollars.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Formats a dollar amount in USD with en-GB grouping. */
export function formatCurrency(amount: number): string {
  return `$${amount.toLocaleString("en-GB", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** A date for people in DD/MM/YYYY. */
export function formatDate(iso: string | Date | null | undefined): string {
  if (!iso) return "–";
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "–";
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${date.getUTCFullYear()}`;
}

/** A date and time for people in DD/MM/YYYY and 24-hour UTC with zone. */
export function formatDateTime(iso: string | Date | null | undefined): string {
  if (!iso) return "–";
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "–";
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${day}/${month}/${date.getUTCFullYear()}, ${hours}:${minutes} UTC`;
}

/** A short timestamp in DD/MM/YYYY HH:MM (24-hour UTC). */
export function formatEntryDate(iso: string | Date | null | undefined): string {
  if (!iso) return "–";
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return "–";
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${formatDate(date)} ${hours}:${minutes}`;
}

/** An elapsed duration in minutes, hours, or days with short en-GB units. */
export function formatElapsed(startedAt: string | Date, now = Date.now()): string {
  const startMs = typeof startedAt === "string" ? Date.parse(startedAt) : startedAt.getTime();
  if (Number.isNaN(startMs)) return "–";
  const minutes = Math.max(0, Math.floor((now - startMs) / 60_000));
  const days = Math.floor(minutes / 1_440);
  const hours = Math.floor((minutes % 1_440) / 60);
  const remainder = minutes % 60;
  if (days > 0) return `${days} d ${hours} h`;
  if (hours > 0) return `${hours} h ${remainder} min`;
  return `${minutes} min`;
}

/** A timestamp in a named time zone (DD/MM/YYYY, HH:MM, 24-hour). */
export function formatZonedDateTime(
  iso: string | Date | null | undefined,
  timeZone: string,
): string {
  if (!iso) return "–";
  const date = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(date.getTime())) return iso as string;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).format(date);
}

/** Formats an integer-cent amount as USD (22800 -> "$228.00"). */
export function formatCentsUsd(cents: number): string {
  return formatCurrency(cents / 100);
}

/** "Last active today" for the same UTC day, otherwise "Last active 08/10/2026". */
export function lastActiveLabel(lastActiveAt: string | null | undefined): string {
  if (!lastActiveAt) return "Not active yet";
  const day = new Date(lastActiveAt);
  const today = new Date();
  const sameDay =
    day.getUTCFullYear() === today.getUTCFullYear() &&
    day.getUTCMonth() === today.getUTCMonth() &&
    day.getUTCDate() === today.getUTCDate();
  return sameDay ? "Last active today" : `Last active ${formatDate(lastActiveAt)}`;
}

/** A signed credit amount: purchases and grants read +20,000, holds and spend read with a minus sign. */
export function formatAmount(amount: number, kind: string): string {
  const magnitude = formatCredits(Math.abs(amount));
  if (kind === "purchase" || kind === "grant" || kind === "release") {
    return `+${magnitude}`;
  }
  if (kind === "unknown") return "?";
  return `−${magnitude}`;
}

/** A credential expiry as DD/MM/YYYY in UTC, or "No expiry" when null. */
export function formatExpiry(value: string | Date | null | undefined): string {
  if (!value) return "No expiry";
  return formatDate(value);
}

/** An export timestamp as DD/MM/YYYY, HH:MM UTC, or "Not available" when null. */
export function formatExportDate(value: string | Date | null | undefined): string {
  if (!value) return "Not available";
  return formatDateTime(value);
}

/** A memory timestamp as DD/MM/YYYY, HH:MM UTC, or "Never" when null. */
export function formatMemoryDate(value: string | Date | null | undefined): string {
  if (!value) return "Never";
  return formatDateTime(value);
}

/** Formats seconds into MM:SS or HH:MM:SS format. */
export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "–";
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  if (hrs > 0) {
    return `${String(hrs).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
  }
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

/** A size in B, KB, or MB with one decimal place. */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

/** Truncates a digest or identifier safely with an ellipsis. */
export function truncateDigest(digest: string, prefixLen = 12, suffixLen = 4): string {
  if (digest.length <= prefixLen + suffixLen + 1) return digest;
  return `${digest.slice(0, prefixLen)}…${digest.slice(-suffixLen)}`;
}

/** A trace span duration in milliseconds ("420 ms") or seconds ("1.42 s"). */
export function formatDurationMs(durationMs: number): string {
  if (durationMs < 1000) {
    return `${durationMs} ms`;
  }
  const seconds = (durationMs / 1000).toFixed(2);
  return `${seconds} s`;
}

/** A micro-USD cost as a USD string with enough precision for small amounts. */
export function formatMicroUSD(microUSD: number): string {
  if (microUSD === 0) return "$0.00";
  const usd = microUSD / 1_000_000;
  if (usd < 0.0001) {
    return `$${usd.toFixed(6)}`;
  }
  return `$${usd.toFixed(4)}`;
}

/** A token count with en-GB thousands grouping. */
export function formatTokens(tokens: number): string {
  return formatCredits(tokens);
}
